import { withNamespaces } from "react-i18next";
import { useEffect, useState } from "react";
import { Col, Row, Input, Label, FormGroup, Form, Button, Modal, ModalBody, ModalHeader, Spinner, UncontrolledPopover, PopoverBody } from "reactstrap";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { withRouter } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { connect } from "react-redux";
import OtherAssetsService from "../../../../apis/OtherAssetsService";
import CommonService from "../../../../apis/CommonService";
import PageHeader from "../../../../components/page-header";
import MultiSelect from "src/modules/data-protection/components/MultiSelect";
import "./style.scss";
import BottyPopup from "./BottyPopup";
import { OTHER_ASSET_TYPES } from "src/modules/data-protection/constants";

const DataProtectionSoftwareCreate = (props) => {
    const { createModalOpen, toggleModal, t } = props;

    const [name, setName] = useState("");
    const [type, setType] = useState("null");
    const [description, setDescription] = useState("");
    const [isPhysical, setIsPhysical] = useState(false);
    const [owner, setOwner] = useState("");
    const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);
    const [selectedModules, setSelectedModules] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [errors, setErrors] = useState({
        name: [],
        type: [],
    });

    const resetForm = () => {
        setName("");
        setType("null");
        setDescription("");
        setIsPhysical(false);
        setOwner("");
        setSelectedLegalEntities([]);
        setSelectedModules([]);
        setErrors({ name: [], type: [] });
    };

    const validateForm = () => {
        const nextErrors = { name: [], type: [] };
        let isValid = true;

        if (!name?.trim()) {
            nextErrors.name = [t("Name is required.")];
            isValid = false;
        }

        if (!type || type === "null") {
            nextErrors.type = [t("Asset type is required.")];
            isValid = false;
        }

        setErrors(nextErrors);
        return isValid;
    };

    const addOtherAssetMutation = useMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetsService.getInstance();
            return await service.create(payload);
        },
        onSuccess: () => {
            toast(t("Other asset added successfully."), { type: "success" });
            toggleModal();
            props?.fetchData?.();
        },
        onError: () => {
            toast(t("An error occurred while adding other asset."), { type: "error" });
        }
    });

    const fetchUsersQuery = useQuery({
        queryKey: ["users"],
        queryFn: async () => {
            const service = CommonService.getInstance();
            return await service.analystAdminsAndAnalysts();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });

    const fetchLegalEntitiesOptions = useQuery({
        queryKey: ["data-protection-enteties-list"],
        queryFn: async () => {
            const service = CommonService.getInstance();
            return await service.legalEntities(props?.Organization?.id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });

    const fetchModulesQuery = useQuery({
        queryKey: ["data-protection-other-assets-modules-list"],
        queryFn: async () => {
            const service = OtherAssetsService.getInstance();
            const response = await service.modules();
            return (response || []).map((m) => ({
                label: m.name,
                value: m.name,
            }));
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onSuccess: (data) => setModuleOptions(data),
    });

    const handleSubmit = () => {
        if (!validateForm()) return;

        addOtherAssetMutation.mutate({
            name: name.trim(),
            type,
            description: description || undefined,
            isPhysical,
            owner: owner ? Number(owner) : undefined,
            legalEntities: selectedLegalEntities.map((item) => String(item.id)),
            modules: selectedModules.map((m) => m.label),
        });
    };

    useEffect(() => {
        if (createModalOpen) {
            resetForm();
        }
    }, [createModalOpen]);

    return (
        <div>
            <Modal size="xl" isOpen={createModalOpen} toggle={toggleModal} className="modal-main-content-Ai-software">
                <ModalHeader toggle={toggleModal} />
                <ModalBody>
                    <div>
                        <PageHeader title={t("Add New Other Asset")} />
                        <Form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                        >
                            <Row className="mb-4">
                                <Col sm="12" md="6" lg="4">
                                    <FormGroup>
                                        <Label for="asset-name" className={errors.name.length ? "text-danger" : ""}>
                                            {t("Name")}
                                        </Label>
                                        <span className="text-danger">*</span>
                                        <Input
                                            className="mt-1 text-capitalize"
                                            id="asset-name"
                                            name="asset-name"
                                            type="text"
                                            value={name}
                                            onChange={(e) => {
                                                setName(e.target.value);
                                                setErrors((prev) => ({ ...prev, name: [] }));
                                            }}
                                            invalid={errors.name.length > 0}
                                        />
                                        {errors.name.map((e) => (
                                            <small key={e} className="text-danger">{e}</small>
                                        ))}
                                    </FormGroup>
                                </Col>

                                <Col sm="12" md="6" lg="4">
                                    <FormGroup>
                                        <Label for="asset-type" className={errors.type.length ? "text-danger" : ""}>
                                            {t("Asset Type")}
                                        </Label>
                                        <span className="text-danger">*</span>
                                        <Input
                                            className="mt-1 text-capitalize"
                                            id="asset-type"
                                            name="asset-type"
                                            type="select"
                                            value={type}
                                            onChange={(e) => {
                                                setType(e.target.value);
                                                setErrors((prev) => ({ ...prev, type: [] }));
                                            }}
                                            invalid={errors.type.length > 0}
                                        >
                                            <option value="null">{t("Select")}</option>
                                            {Object.keys(OTHER_ASSET_TYPES).map((itemKey) => (
                                                <option key={itemKey} value={itemKey}>
                                                    {t(OTHER_ASSET_TYPES[itemKey].title)}
                                                </option>
                                            ))}
                                        </Input>
                                        {errors.type.map((e) => (
                                            <small key={e} className="text-danger">{e}</small>
                                        ))}
                                    </FormGroup>
                                </Col>

                                <Col sm="12" md="6" lg="4">
                                    <FormGroup>
                                        <Label for="owner">{t("Owner")}</Label>
                                        <Input
                                            className="mt-1 text-capitalize"
                                            id="owner"
                                            name="owner"
                                            type="select"
                                            value={owner}
                                            onChange={(e) => setOwner(e.target.value)}
                                        >
                                            <option value="">{t("Select")}</option>
                                            {fetchUsersQuery.data?.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.first_name} {user.last_name}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col sm="12" md="12" lg="12">
                                    <FormGroup>
                                        <Label for="description">{t("Description")}</Label>
                                        <Input
                                            className="mt-1"
                                            id="description"
                                            name="description"
                                            type="textarea"
                                            rows="3"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col sm="12" md="6" lg="4">
                                    <FormGroup>
                                        <Label for="entities">{t("Legal Entities")}</Label>
                                        <MultiSelect
                                            options={fetchLegalEntitiesOptions.data || []}
                                            selectedValues={selectedLegalEntities}
                                            onChange={setSelectedLegalEntities}
                                            placeholder={t("Select...")}
                                            isDisabled={fetchLegalEntitiesOptions.isLoading}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col sm="12" md="6" lg="8">
                                    <FormGroup>
                                        <Label for="selected-modules">
                                            {t("Modules")}
                                            <>
                                                <span
                                                    id="section-info-button-other-asset-module-create"
                                                    className="section-info-button"
                                                >
                                                    <i className="ri-information-line" />
                                                </span>

                                                <UncontrolledPopover
                                                    placement="bottom"
                                                    trigger="hover"
                                                    target="section-info-button-other-asset-module-create"
                                                >
                                                    <PopoverBody>
                                                        <span>
                                                            {t(
                                                                "An Asset Module is an optional label used to tag a part or area of this other asset. You can select existing modules or create new ones to organize the asset record."
                                                            )}
                                                        </span>
                                                    </PopoverBody>
                                                </UncontrolledPopover>
                                            </>
                                        </Label>
                                        <CreatableSelect
                                            className="mt-1 text-capitalize"
                                            placeholder={t("Select") + "..."}
                                            id="selected-modules"
                                            name="selected-modules"
                                            value={selectedModules}
                                            isMulti
                                            onChange={(newModules) => setSelectedModules(newModules || [])}
                                            onCreateOption={(label) => {
                                                const option = { label, value: label };
                                                setModuleOptions((prev) => [...prev, option]);
                                                setSelectedModules((prev) => [...prev, option]);
                                            }}
                                            options={moduleOptions}
                                            isLoading={fetchModulesQuery.isLoading || fetchModulesQuery.isFetching}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col sm="12" md="6" lg="4">
                                    <FormGroup className="d-flex align-items-start flex-column">
                                        <Label for="is-physical" className="d-flex">
                                            {t("Physical Asset")}
                                        </Label>
                                        <FormGroup className="d-flex align-items-center" style={{ height: "38px" }}>
                                            <Input
                                                className="mt-1"
                                                id="is-physical"
                                                name="is-physical"
                                                type="checkbox"
                                                checked={isPhysical}
                                                onChange={(e) => setIsPhysical(e.target.checked)}
                                            />
                                            <Label check for="is-physical" style={{ marginLeft: "10px" }}>
                                                {t("Is Physical")}
                                            </Label>
                                        </FormGroup>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="6" lg="4">
                                    <Button disabled={addOtherAssetMutation.isLoading} className="btn-primary" type="submit">
                                        {addOtherAssetMutation.isLoading && (
                                            <Spinner size="sm" color="white" className="me-1" />
                                        )}

                                        {t("Save")}
                                    </Button>
                                    <Button onClick={toggleModal} className="btn btn-danger mx-2">
                                        {t("Back to List")}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </ModalBody>
            </Modal>

            <BottyPopup 
                t={t} 
                showBottyPopup={addOtherAssetMutation.isLoading}
            />
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { Organization } = state;
    return { Organization };
};

export default withNamespaces()(withRouter(connect(mapStatetoProps)(DataProtectionSoftwareCreate)));
